/**
 * Image Optimization Utilities
 * Provides responsive image handling, format detection, and lazy loading
 */

// Supported modern image formats in order of preference
const MODERN_FORMATS = ['avif', 'webp'];
const FALLBACK_FORMATS = ['jpg', 'jpeg', 'png'];

/**
 * Generate responsive image sources with multiple formats and sizes
 * @param {string} baseUrl - Base image URL
 * @param {Object} options - Configuration options
 * @returns {Object} Image sources and metadata
 */
export function generateResponsiveImageSources(baseUrl, options = {}) {
  const {
    sizes = [320, 640, 768, 1024, 1280, 1920],
    formats = ['avif', 'webp', 'jpg'],
    quality = 85,
    fallbackFormat = 'jpg'
  } = options;

  if (!baseUrl || baseUrl.includes('placehold.co')) {
    return {
      sources: [],
      fallback: baseUrl,
      placeholder: generatePlaceholder(options.width, options.height)
    };
  }

  const sources = [];
  const baseUrlWithoutExt = baseUrl.replace(/\.[^/.]+$/, '');
  
  // Generate sources for each format
  formats.forEach(format => {
    const srcset = sizes
      .map(size => `${baseUrlWithoutExt}_${size}w.${format} ${size}w`)
      .join(', ');
    
    sources.push({
      srcset,
      type: `image/${format}`,
      sizes: generateSizesAttribute(options.breakpoints)
    });
  });

  return {
    sources,
    fallback: `${baseUrlWithoutExt}.${fallbackFormat}`,
    placeholder: generatePlaceholder(options.width, options.height),
    aspectRatio: calculateAspectRatio(options.width, options.height)
  };
}

/**
 * Generate sizes attribute for responsive images
 * @param {Object} breakpoints - Breakpoint configuration
 * @returns {string} Sizes attribute value
 */
export function generateSizesAttribute(breakpoints = {}) {
  const defaultBreakpoints = {
    mobile: '(max-width: 640px) 100vw',
    tablet: '(max-width: 1024px) 50vw',
    desktop: '33vw'
  };
  
  const merged = { ...defaultBreakpoints, ...breakpoints };
  return Object.values(merged).join(', ');
}

/**
 * Calculate aspect ratio from width and height
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {string} CSS aspect ratio value
 */
function calculateAspectRatio(width, height) {
  if (!width || !height) return '4/3'; // Default aspect ratio
  
  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(width, height);
  
  return `${width / divisor}/${height / divisor}`;
}

/**
 * Generate placeholder image URL
 * @param {number} width - Placeholder width
 * @param {number} height - Placeholder height
 * @returns {string} Placeholder URL
 */
function generatePlaceholder(width = 600, height = 400) {
  const svgContent = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
    <rect width="100%" height="100%" fill="#f3f4f6"/>
    <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#9ca3af" font-family="Arial, sans-serif" font-size="16">
      กำลังโหลด...
    </text>
  </svg>`;
  
  try {
    return `data:image/svg+xml;base64,${btoa(svgContent)}`;
  } catch (e) {
    // Fallback for environments that don't support btoa or have encoding issues
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
  }
}

/**
 * Check if browser supports modern image formats
 * @returns {Object} Format support detection
 */
export function detectImageFormatSupport() {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  const support = {
    webp: false,
    avif: false
  };

  // Check WebP support
  try {
    support.webp = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch (e) {
    support.webp = false;
  }

  // Check AVIF support (more complex detection)
  const avifImage = new Image();
  avifImage.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A=';
  
  return new Promise((resolve) => {
    avifImage.onload = () => {
      support.avif = true;
      resolve(support);
    };
    avifImage.onerror = () => {
      support.avif = false;
      resolve(support);
    };
    
    // Timeout fallback
    setTimeout(() => {
      resolve(support);
    }, 100);
  });
}

/**
 * Lazy loading intersection observer
 */
class LazyImageLoader {
  constructor(options = {}) {
    this.options = {
      rootMargin: '50px 0px',
      threshold: 0.01,
      ...options
    };
    
    this.observer = null;
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        this.options
      );
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        this.loadImage(img);
        this.observer.unobserve(img);
      }
    });
  }

  loadImage(img) {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    
    if (src) {
      img.src = src;
    }
    
    if (srcset) {
      img.srcset = srcset;
    }
    
    img.classList.remove('lazy');
    img.classList.add('loaded');
    
    // Trigger load event for any listeners
    img.dispatchEvent(new Event('lazyload'));
  }

  observe(img) {
    if (this.observer) {
      this.observer.observe(img);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.loadImage(img);
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Global lazy loader instance
export const lazyLoader = new LazyImageLoader();

/**
 * Preload critical images
 * @param {Array} imageUrls - Array of image URLs to preload
 * @param {Object} options - Preload options
 */
export function preloadImages(imageUrls, options = {}) {
  const { priority = 'high', as = 'image' } = options;
  
  imageUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = as;
    link.href = url;
    
    if (priority) {
      link.fetchPriority = priority;
    }
    
    document.head.appendChild(link);
  });
}

/**
 * Create optimized image element with lazy loading
 * @param {Object} config - Image configuration
 * @returns {HTMLElement} Optimized image element
 */
export function createOptimizedImage(config) {
  const {
    src,
    alt,
    className = '',
    lazy = true,
    width,
    height,
    sizes,
    formats = ['webp', 'jpg']
  } = config;

  const picture = document.createElement('picture');
  const imageData = generateResponsiveImageSources(src, {
    width,
    height,
    formats,
    sizes
  });

  // Add source elements for each format
  imageData.sources.forEach(source => {
    const sourceEl = document.createElement('source');
    sourceEl.srcset = lazy ? '' : source.srcset;
    sourceEl.type = source.type;
    sourceEl.sizes = source.sizes;
    
    if (lazy) {
      sourceEl.dataset.srcset = source.srcset;
    }
    
    picture.appendChild(sourceEl);
  });

  // Add fallback img element
  const img = document.createElement('img');
  img.alt = alt;
  img.className = `${className} ${lazy ? 'lazy' : ''}`;
  
  if (lazy) {
    img.src = imageData.placeholder;
    img.dataset.src = imageData.fallback;
  } else {
    img.src = imageData.fallback;
  }
  
  if (width) img.width = width;
  if (height) img.height = height;
  
  // Set aspect ratio for layout stability
  img.style.aspectRatio = imageData.aspectRatio;
  
  picture.appendChild(img);
  
  // Initialize lazy loading
  if (lazy) {
    lazyLoader.observe(img);
  }
  
  return picture;
}

/**
 * Image compression utility for client-side optimization
 * @param {File} file - Image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<Blob>} Compressed image blob
 */
export function compressImage(file, options = {}) {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.85,
    format = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(resolve, format, quality);
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Performance monitoring for image loading
 */
export class ImagePerformanceMonitor {
  constructor() {
    this.metrics = {
      totalImages: 0,
      loadedImages: 0,
      failedImages: 0,
      averageLoadTime: 0,
      totalLoadTime: 0
    };
  }

  trackImageLoad(img, startTime = performance.now()) {
    this.metrics.totalImages++;
    
    const onLoad = () => {
      const loadTime = performance.now() - startTime;
      this.metrics.loadedImages++;
      this.metrics.totalLoadTime += loadTime;
      this.metrics.averageLoadTime = this.metrics.totalLoadTime / this.metrics.loadedImages;
      
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
    };
    
    const onError = () => {
      this.metrics.failedImages++;
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
    };
    
    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
  }

  getMetrics() {
    return {
      ...this.metrics,
      successRate: this.metrics.totalImages > 0 
        ? (this.metrics.loadedImages / this.metrics.totalImages) * 100 
        : 0
    };
  }

  reset() {
    this.metrics = {
      totalImages: 0,
      loadedImages: 0,
      failedImages: 0,
      averageLoadTime: 0,
      totalLoadTime: 0
    };
  }
}

// Global performance monitor
export const imagePerformanceMonitor = new ImagePerformanceMonitor();

/**
 * Optimize image URL with parameters
 * @param {string} url - Original image URL
 * @param {Object} options - Optimization options
 * @returns {string} Optimized URL
 */
export function optimizeImageUrl(url, options = {}) {
  if (!url || url.includes('placehold.co') || url.startsWith('data:')) {
    return url;
  }

  const {
    width,
    height,
    quality = 85,
    format
  } = options;

  // For external URLs, return as-is (would need CDN integration)
  if (url.startsWith('http') && !url.includes(window.location.hostname)) {
    return url;
  }

  // Build query parameters
  const params = new URLSearchParams();
  
  if (width) params.set('w', width);
  if (height) params.set('h', height);
  if (quality !== 85) params.set('q', quality);
  if (format) params.set('f', format);

  const queryString = params.toString();
  if (!queryString) return url;

  return url + (url.includes('?') ? '&' : '?') + queryString;
}

/**
 * Get optimal image dimensions based on container
 * @param {HTMLElement} container - Container element
 * @returns {Object} Optimal dimensions
 */
export function getOptimalImageDimensions(container) {
  if (!container) return { width: 600, height: 400 };

  const rect = container.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;

  return {
    width: Math.ceil(rect.width * dpr),
    height: Math.ceil(rect.height * dpr)
  };
}

/**
 * Setup lazy loading for an image element
 * @param {HTMLImageElement} img - Image element
 * @param {Object} options - Lazy loading options
 * @returns {Function} Cleanup function
 */
export function lazyLoadImage(img, options = {}) {
  const {
    rootMargin = '50px 0px',
    threshold = 0.01,
    placeholder
  } = options;

  if (!img || !('IntersectionObserver' in window)) {
    // Fallback: load immediately
    if (img && img.dataset.src) {
      img.src = img.dataset.src;
    }
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;
          
          // Load the image
          if (target.dataset.src) {
            target.src = target.dataset.src;
          }
          
          // Load srcset if available
          if (target.dataset.srcset) {
            target.srcset = target.dataset.srcset;
          }
          
          // Clean up
          target.classList.remove('lazy');
          target.classList.add('loaded');
          observer.unobserve(target);
        }
      });
    },
    { rootMargin, threshold }
  );

  // Set placeholder if provided
  if (placeholder && !img.src) {
    img.src = placeholder;
  }

  observer.observe(img);

  // Return cleanup function
  return () => {
    observer.unobserve(img);
    observer.disconnect();
  };
}